import { collection, getDocs, doc, updateDoc } from "firebase/firestore";
import { getFunctions, httpsCallable } from "firebase/functions";
import { db } from "../firebase";
import {
  parseClientInfoByPlatform,
  type StoredClientInfo,
} from "../utils/clientInfo";

export type AdminUserEmail = {
  id: string;
  email: string;
  name?: string;
  admin: boolean;
  banned: boolean;
  // When the user joined (`createdAt` in Firestore), as epoch millis. Null for
  // older accounts created before we started stamping a join date.
  createdAt: number | null;
  // App version per platform the user has opened the app on (ios/android/web).
  clients: StoredClientInfo[];
};

export type AdminUserSort = "joined_desc" | "joined_asc" | "email";

/**
 * Normalises a Firestore `createdAt` value (a Timestamp, a raw {seconds}
 * object, or a number) to epoch millis. Returns null when absent/unreadable.
 */
function toMillis(value: unknown): number | null {
  if (value == null) return null;
  if (typeof value === "number") return value;
  const ts = value as { toMillis?: () => number; seconds?: number };
  if (typeof ts.toMillis === "function") return ts.toMillis();
  if (typeof ts.seconds === "number") return ts.seconds * 1000;
  return null;
}

/**
 * Pure, order-stable sort for the admin user list. Users with no join date
 * always sort to the end, regardless of direction.
 */
export function sortAdminUsers(
  users: AdminUserEmail[],
  mode: AdminUserSort,
): AdminUserEmail[] {
  const copy = [...users];
  if (mode === "email") {
    return copy.sort((a, b) => a.email.localeCompare(b.email));
  }
  return copy.sort((a, b) => {
    if (a.createdAt == null && b.createdAt == null) return 0;
    if (a.createdAt == null) return 1;
    if (b.createdAt == null) return -1;
    return mode === "joined_asc"
      ? a.createdAt - b.createdAt
      : b.createdAt - a.createdAt;
  });
}

export async function getUserEmails(): Promise<AdminUserEmail[]> {
  const snapshot = await getDocs(collection(db, "users"));

  const users = snapshot.docs
    .map((userDoc) => {
      const data = userDoc.data() as {
        email?: string;
        name?: string;
        admin?: boolean;
        banned?: boolean;
        createdAt?: unknown;
        clientInfoByPlatform?: unknown;
      };

      return {
        id: userDoc.id,
        email: data.email?.trim() || "",
        name: data.name?.trim(),
        admin: data.admin === true,
        banned: data.banned === true,
        createdAt: toMillis(data.createdAt),
        clients: parseClientInfoByPlatform(data.clientInfoByPlatform),
      };
    })
    .filter((user) => user.email);

  // Default to newest-joined first so admins can grab recent sign-ups for the
  // email list without scrolling; the screen lets them re-sort.
  return sortAdminUsers(users, "joined_desc");
}

export async function toggleUserBan(
  userId: string,
  currentBannedState: boolean,
): Promise<void> {
  const userRef = doc(db, "users", userId);
  await updateDoc(userRef, {
    banned: !currentBannedState,
  });
}

/**
 * Permanently deletes another user's account (Meldpot #102, admin only). Calls
 * the same backend function as self-delete with an explicit targetUid; the
 * function enforces that the caller is an admin.
 */
export async function deleteUser(userId: string): Promise<void> {
  const functions = getFunctions();
  const callable = httpsCallable(functions, "deleteUserAccount");
  await callable({ targetUid: userId });
}

export async function getFlagsCountMap(): Promise<Record<string, number>> {
  try {
    const snapshot = await getDocs(collection(db, "flags"));
    const counts: Record<string, number> = {};
    snapshot.docs.forEach((docSnap) => {
      const data = docSnap.data();
      const flaggedUserId = data.flaggedUserId;
      if (flaggedUserId) {
        counts[flaggedUserId] = (counts[flaggedUserId] || 0) + 1;
      }
    });
    return counts;
  } catch (error) {
    console.error("Error fetching flags:", error);
    return {};
  }
}

export type FlagReport = {
  id: string;
  flaggedUserId: string;
  flaggedUserName: string;
  flaggedByUserId: string;
  flaggedByUserName: string;
  roomId: string;
  roomName: string;
  reason?: string;
  details?: string;
  createdAt?: any;
};

export async function getAllFlagReports(): Promise<FlagReport[]> {
  try {
    const snapshot = await getDocs(collection(db, "flags"));
    return snapshot.docs.map((docSnap) => {
      const data = docSnap.data();
      return {
        id: docSnap.id,
        flaggedUserId: data.flaggedUserId || "",
        flaggedUserName: data.flaggedUserName || "",
        flaggedByUserId: data.flaggedByUserId || "",
        flaggedByUserName: data.flaggedByUserName || "",
        roomId: data.roomId || "",
        roomName: data.roomName || "",
        reason: data.reason || "",
        details: data.details || "",
        createdAt: data.createdAt,
      };
    });
  } catch (error) {
    console.error("Error fetching all flag reports:", error);
    return [];
  }
}
